import React, { useState } from 'react';

import ItemCard from '../components/ItemCard';
import Modal from '../components/Modal';
import Window from '../components/Window';
import PopupContainer from '../components/PopupContainer';
import FolderSelect from '../components/FolderSelect';
import { AccentButton } from '../components/Button';
import { DeleteFileConfirmation, DeleteForeverConfirmation } from '../components/ConfirmationPopup';
import { CopyIcon, TrashIcon, CornerUpLeftIcon, ExpandIcon, PinIcon } from '../icons';
import OldTextEditor from '../../components/OldTextEditor';

import { useItemActions } from '../hooks/useItemActions';
import { useFolders } from '../../hooks/useFolder';
import { useStoredText } from '../../hooks/useStoredText';
import { formatRelativeTime } from '../../utils';

const TextContent = ({ item }) => {
  const { content } = useStoredText(item.contentUrl);
  return (
    <>
      <div className="p-2 h-full flex items-start">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-[#121212] dark:text-[#D0D0D0] whitespace-pre overflow-hidden">
            {content}
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-5 bg-gradient-to-t from-[#FFFFFF] dark:from-[#121212] to-transparent pointer-events-none" />
      <div className="absolute top-0 right-0 bottom-0 w-5 bg-gradient-to-l from-[#FFFFFF] dark:from-[#121212] to-transparent pointer-events-none" />
    </>
  );
};

const TextModalContent = ({ item }) => {
  const { content } = useStoredText(item.contentUrl);
  return (
    <div className="w-full h-full flex-1 min-h-0">
      <OldTextEditor
        content={content}
        className="w-full h-full min-h-0"
        readOnly={false}
        language="plain"
        onChange={(value) => console.log('Text changed:', value)}
      />
    </div>
  );
};

const ImageContent = ({ imageUrl }) => (
  <div className="w-full h-full flex items-center justify-center">
    {imageUrl ? (
      <img
        src={imageUrl}
        alt="Clipboard content"
        crossOrigin="anonymous"
        className="w-full h-full object-cover rounded-2xl"
      />
    ) : (
      <div className="w-full h-full bg-gray-100 rounded-2xl flex items-center justify-center">
        <div className="text-gray-400 text-sm">No image</div>
      </div>
    )}
  </div>
);

const ImageModalContent = ({ item }) => {
  const handleExpand = (e) => {
    e.stopPropagation();
    if (item.contentUrl) window.open(item.contentUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="w-full h-full rounded-2xl flex items-center justify-center relative group/modal">
      <img src={item.contentUrl} alt="" className="max-w-full max-h-full object-contain" />

      {/* Expand button */}
      <div style={{ position: 'absolute', bottom: 12, right: 12, zIndex: 10 }}>
        <button
          onClick={handleExpand}
          className="
            flex items-center justify-center w-8 h-8 rounded-lg
            bg-[#F1F1F1] dark:bg-[#2A2A2A]
            hover:bg-[#F1F1F1]/10 dark:hover:bg-[#2A2A2A]/10
            transition-all duration-150
          "
        >
          <ExpandIcon size={16} color="currentColor" className="text-[#121212] dark:text-[#D0D0D0]" />
        </button>
      </div>
    </div>
  );
};

export default function ItemRenderer({ item, isTrash = false, isSelecting = false, isSelected = false, onToggleSelect }) {
  const { copy, remove, restore, deletePermanently, move, pin } = useItemActions(item);
  const { folders } = useFolders();

  const folderOptions = folders.map(folder => ({
    value: folder.id,
    label: folder.name,
    color: 'rgba(74, 186, 98, 1)',
    fill: 'rgba(74, 186, 98, 0.2)',
  }));

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(
    () => folderOptions.find(f => f.value === item.raw?.clipboard_id) || null
  );

  const handleDelete = () => {
    if (isTrash) setIsConfirmingDelete(true);
    else setIsConfirmingDelete(true);
  };

  const handleSave = async () => {
    if (selectedFolder && selectedFolder.value !== item.raw?.clipboard_id) {
      await move(selectedFolder.value);
    }
    setIsModalOpen(false);
  };

  const isPinned = item.pinned ?? false;

  const actions = [
    !isTrash && {
      key: 'pin',
      // Unpinned: slightly larger icon as an invitation; pinned: normal size, accent colour
      icon: (iconProps) => (
        <PinIcon
          size={16}
          filled={isPinned}
          {...iconProps}
        />
      ),
      onClick: () => pin(!isPinned),
      ariaLabel: isPinned ? 'Unpin item' : 'Pin to top',
      ariaPressed: isPinned,
    },
    { key: 'copy', icon: CopyIcon, onClick: copy },
    isTrash && { key: 'restore', icon: CornerUpLeftIcon, onClick: restore },
    { key: 'delete', icon: TrashIcon, onClick: handleDelete },
  ].filter(Boolean);

  const date = item.createdAt ? formatRelativeTime(item.createdAt) : 'Unknown date';
  const itemType = item.getType?.();

  return (
    <>
      <ItemCard
        date={date}
        device={item.raw?.source_device}
        actions={actions}
        pinned={isPinned}
        isSelecting={isSelecting}
        isSelected={isSelected}
        onToggleSelect={onToggleSelect}
        onClick={() => setIsModalOpen(true)}
      >
        {itemType === 'text'
          ? <TextContent item={item} />
          : <ImageContent imageUrl={item.contentUrl} />
        }
      </ItemCard>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl">
        <Window
          bottomLeft={!isTrash && folderOptions.length > 0 ? (
            <FolderSelect
              value={selectedFolder?.value}
              onChange={setSelectedFolder}
              options={folderOptions}
              onNewFolder={() => {}}
            />
          ) : null}
          bottomRight={(
            <>
              <AccentButton variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</AccentButton>
              <AccentButton variant="primary" onClick={handleSave}>Save</AccentButton>
            </>
          )}
        >
          {itemType === 'text'
            ? <TextModalContent item={item} />
            : <ImageModalContent item={item} />
          }
        </Window>
      </Modal>

      <PopupContainer isOpen={isConfirmingDelete} onClose={() => setIsConfirmingDelete(false)}>
        {isTrash ? (
          <DeleteForeverConfirmation
            onClose={() => setIsConfirmingDelete(false)}
            onConfirm={async () => { await deletePermanently(); setIsConfirmingDelete(false); }}
          />
        ) : (
          <DeleteFileConfirmation
            onClose={() => setIsConfirmingDelete(false)}
            onConfirm={async () => { await remove(); setIsConfirmingDelete(false); }}
          />
        )}
      </PopupContainer>
    </>
  );
}
